import { useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';

import { type MostPopularBetsResponse } from '../../interfaces';
import { fetchMostPopularBetsData, getMostPopularBetsCacheKey } from '../api/most-popular-bets.service';
import { useMostPopularBetsProps } from '../context/MostPopularBetsContext';
import { resolveQueryTarget } from '../utils/resolveQueryTarget';

export const ALL_SPORTS_VALUE = -1;

export const useMostPopularBetsAPI = () => {
  const props = useMostPopularBetsProps();
  const { apiKey, countryCode, dataApiEndpoint, includeAll, limit, repub, sports, subdivisionCode } = props;

  const target = resolveQueryTarget(props);
  const isSingleTarget = target.kind !== 'sports';

  const defaultCategoryGroupId = includeAll ? ALL_SPORTS_VALUE : (sports[0]?.categoryGroupId ?? ALL_SPORTS_VALUE);
  const [selectedCategoryGroupId, setSelectedCategoryGroupId] = useState<number>(defaultCategoryGroupId);

  const headers = { apiKey, countryCode, repub, subdivisionCode };

  const isAllSports = selectedCategoryGroupId === ALL_SPORTS_VALUE;
  const params = (() => {
    switch (target.kind) {
      case 'subevent':
        return { limit, subeventId: target.subeventId };
      case 'events':
        return { eventIds: target.eventIds, limit };
      default: {
        if (isAllSports && includeAll) {
          return { categoryGroupIds: sports.map(s => s.categoryGroupId), includeAll: true, limit };
        }
        const categoryGroupId = isAllSports ? undefined : selectedCategoryGroupId;
        return { categoryGroupId, limit };
      }
    }
  })();

  const { data, error } = useSuspenseQuery<MostPopularBetsResponse | null>({
    queryFn: () => fetchMostPopularBetsData(dataApiEndpoint, params, headers, { logger: console }),
    queryKey: getMostPopularBetsCacheKey(params),
  });

  const bets = data?.bets ?? [];
  const allSportsBets = data?.allSportsBets ?? [];

  return {
    allSportsBets,
    bets,
    error,
    includeAll,
    isSingleTarget,
    selectedCategoryGroupId,
    setSelectedCategoryGroupId,
    sports,
  };
};

export default useMostPopularBetsAPI;
